import { useId } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { WOLF } from './wolfData'

/**
 * The CADINUdex signature: a husky that resembles a wolf, howling at the moon.
 * Everything is drawn here as SVG (no raster, no third-party art): a night sky of stars, a moon with a halo, a rock ridge, and the low-poly
 * wolf (facets in the spirit of the CADINU logo's faceted husky head) rim-lit by the moon, with three howl rings leaving its muzzle.
 * Decorative only: aria-hidden, no pointer events. Motion is two slow loops (twinkle, howl) and stops under prefers-reduced-motion.
 */

type SceneName = 'hero' | 'heroM' | 'banner' | 'bannerM' | 'spot' | 'wolfcol'
type Scene = {
  vb: [number, number]
  moon: { x: number; y: number; r: number } | null
  /** left edge of the wolf's box and its scale; the feet stand on `ground` */
  wolf: { x: number; scale: number; ground: number } | null
  stars: number
  /** how far the howl rings travel (1 = the wolf's own size) */
  ring: number
}
/**
 * Each scene is drawn in the aspect ratio of the box that holds it, so nothing important is ever cropped: a wide card gets a wide scene
 * (desktop), a thin strip gets its own (phone). The pages render the desktop and the phone scene and let CSS show one of them.
 */
const SCENES: Record<SceneName, Scene> = {
  hero: { vb: [1200, 340], moon: { x: 716, y: 118, r: 62 }, wolf: { x: 872, scale: 0.6, ground: 330 }, stars: 80, ring: 0.8 },
  heroM: { vb: [390, 200], moon: { x: 96, y: 84, r: 40 }, wolf: { x: 214, scale: 0.36, ground: 192 }, stars: 34, ring: 0.45 },
  banner: { vb: [1200, 360], moon: null, wolf: { x: 872, scale: 0.56, ground: 346 }, stars: 70, ring: 0.8 },
  bannerM: { vb: [390, 144], moon: { x: 168, y: 52, r: 22 }, wolf: { x: 244, scale: 0.24, ground: 136 }, stars: 28, ring: 0.4 },
  spot: { vb: [420, 232], moon: { x: 104, y: 70, r: 34 }, wolf: { x: 214, scale: 0.36, ground: 222 }, stars: 30, ring: 0.5 },
  /** the token hero's wolf column: a FIXED-size box (the page reserves exactly its width), the moon at its upper left, the halo may spill out of the box */
  wolfcol: { vb: [360, 340], moon: { x: 40, y: 64, r: 30 }, wolf: { x: 70, scale: 0.56, ground: 330 }, stars: 0, ring: 0.8 },
}

/** The rock ridge under the wolf, its bumps scaled with the wolf so a small scene gets small rocks. */
const ridge = (x: number, g: number, k: number, w: number, h: number) =>
  `M${x - 210 * k},${h} L${x - 210 * k},${g + 26 * k} L${x - 130 * k},${g + 12 * k} L${x - 40 * k},${g + 18 * k} L${x + 10 * k},${g - 2 * k} L${x + 90 * k},${g + 2 * k} L${x + 230 * k},${g + 3 * k} L${x + 330 * k},${g - 14 * k} L${x + 470 * k},${g + 10 * k} L${w + 40},${g + 30 * k} L${w + 40},${h} Z`

/** Deterministic pseudo-random (same on server and client, so markup never differs between the two). */
/** darkest to moon-lit, matching WOLF.facets */
const FACET_FILLS = ['#04050d', '#070a1a', '#0a0f26', '#101836', '#172046']
const M32 = 4294967296
const lcg = (seed: number) => {
  let s = ((seed % M32) + M32) % M32
  return () => {
    s = (((Math.imul(s, 1664525) + 1013904223) % M32) + M32) % M32
    return s / M32
  }
}
const starsFor = (name: SceneName, scene: Scene) => {
  const rnd = lcg({ hero: 7, heroM: 11, banner: 21, bannerM: 27, spot: 33, wolfcol: 41 }[name])
  const [w, h] = scene.vb
  return Array.from({ length: scene.stars }, (_, i) => ({
    x: Math.round(rnd() * w * 10) / 10,
    y: Math.round(rnd() ** 1.5 * h * 0.82 * 10) / 10,
    r: rnd() > 0.93 ? 1.7 : rnd() > 0.6 ? 1.1 : 0.7,
    o: Math.round((0.35 + rnd() * 0.6) * 100) / 100,
    tw: i % 6 === 0 ? (i % 4) + 1 : 0, // every sixth star twinkles, with one of four delays
  }))
}

/** The moon's gradients (halo, lit disc, terminator shade), shared by the scenes and the compact icon. */
function MoonDefs({ id }: { id: string }) {
  return (
    <>
      <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor="#b9ccff" stopOpacity="0.5" />
        <stop offset="0.35" stopColor="#8c9cf0" stopOpacity="0.22" />
        <stop offset="1" stopColor="#7e00fe" stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-disc`} cx="38%" cy="34%" r="75%">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="0.55" stopColor="#dbe6ff" />
        <stop offset="1" stopColor="#9fb4ec" />
      </radialGradient>
      <radialGradient id={`${id}-shade`} cx="36%" cy="32%" r="80%">
        <stop offset="0.6" stopColor="#0a0e22" stopOpacity="0" />
        <stop offset="1" stopColor="#0a0e22" stopOpacity="0.42" />
      </radialGradient>
    </>
  )
}
/** The moon: halo (`halo` x the radius), lit disc, six craters, shade and a thin rim. Needs <MoonDefs id> in the same <svg>. */
function MoonBody({ id, x, y, r, halo }: { id: string; x: number; y: number; r: number; halo: number }) {
  return (
    <g data-testid="moon">
      <circle cx={x} cy={y} r={r * halo} fill={`url(#${id}-halo)`} />
      <circle cx={x} cy={y} r={r} fill={`url(#${id}-disc)`} />
      {[[-0.3, -0.2, 0.2], [0.24, 0.12, 0.26], [-0.06, 0.46, 0.14], [0.38, -0.36, 0.11], [-0.44, 0.26, 0.1], [0.05, -0.5, 0.09]].map(([cx, cy, cr]) => (
        <circle key={`${cx}${cy}`} cx={x + cx * r} cy={y + cy * r} r={cr * r} fill="#7f93cf" opacity="0.17" />
      ))}
      <circle cx={x} cy={y} r={r} fill={`url(#${id}-shade)`} />
      <circle cx={x} cy={y} r={r} fill="none" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.2" />
    </g>
  )
}
/** The moon alone, for compact empty states: a 120 px box (the halo fades out inside it, so nothing is ever clipped), the disc 52 px across. Decorative. */
export function MoonIcon({ size = 120 }: { size?: number }) {
  const id = useId().replace(/:/g, '')
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true" focusable="false" style={{ display: 'block' }} data-testid="moon-icon">
      <defs><MoonDefs id={id} /></defs>
      <MoonBody id={id} x={60} y={60} r={26} halo={2.2} />
    </svg>
  )
}

const twinkle = keyframes`
  0%, 100% { opacity: var(--o); }
  50% { opacity: calc(var(--o) * 0.25); }
`
const howl = keyframes`
  0% { transform: scale(0.35); opacity: 0; }
  18% { opacity: 0.6; }
  100% { transform: scale(1.45); opacity: 0; }
`
const Svg = styled.svg<{ $still?: boolean; $bleed?: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  ${({ $bleed }) => ($bleed ? 'overflow: visible;' : '')}
  .tw {
    animation: ${twinkle} 4.5s ease-in-out infinite;
  }
  .tw1 { animation-delay: -0.6s; }
  .tw2 { animation-delay: -1.9s; }
  .tw3 { animation-delay: -3.1s; }
  .tw4 { animation-delay: -4.0s; }
  .ring {
    transform-origin: 0 0;
    animation: ${howl} 4.4s cubic-bezier(0.16, 1, 0.3, 1) infinite;
  }
  .ring2 { animation-delay: 1.45s; }
  .ring3 { animation-delay: 2.9s; }
  ${({ $still }) =>
    $still &&
    css`
      .tw, .ring { animation: none; }
      .ring { opacity: 0.25; transform: scale(1); }
    `}
  @media (prefers-reduced-motion: reduce) {
    .tw, .ring { animation: none; }
    .ring { opacity: 0.25; transform: scale(1); }
  }
`

const Pane = styled.div<{ $desk?: boolean }>`
  position: absolute;
  inset: 0;
  display: ${({ $desk }) => ($desk ? 'none' : 'block')};
  ${({ theme }) => theme.mediaQueries.md} {
    display: ${({ $desk }) => ($desk ? 'block' : 'none')};
  }
`
/** The wide scene from tablet width up, the strip scene on a phone: both are drawn for their own box, CSS picks one. */
export function ResponsiveScene({ desktop, mobile, moon = true, still, desktopWolf = true }: { desktop: SceneName; mobile: SceneName; moon?: boolean; still?: boolean; desktopWolf?: boolean }) {
  return (
    <>
      <Pane $desk><MoonScene scene={desktop} moon={moon && desktopWolf} wolf={desktopWolf} still={still} align="xMaxYMax" /></Pane>
      <Pane><MoonScene scene={mobile} moon={moon} still={still} align="xMidYMax" /></Pane>
    </>
  )
}

export type MoonSceneProps = {
  scene?: SceneName
  /** draw the moon disc (the token page uses the token's logo as the moon instead) */
  moon?: boolean
  /** hide the wolf for compact backgrounds */
  wolf?: boolean
  /** freeze the motion (screenshots, tests) */
  still?: boolean
  /** where the SVG is anchored when its box is cropped */
  align?: string
  /** let the moon's halo spill outside the box (a fixed-size scene that sits on a card) */
  bleed?: boolean
  className?: string
}

export default function MoonScene({ scene = 'hero', moon = true, wolf = true, still, align = 'xMaxYMax', bleed, className }: MoonSceneProps) {
  const id = useId().replace(/:/g, '')
  const sc = SCENES[scene]
  const [w, h] = sc.vb
  const stars = starsFor(scene, sc)
  const wf = wolf ? sc.wolf : null
  const noseX = wf ? wf.x + WOLF.nose.x * wf.scale : 0
  const noseY = wf ? wf.ground - (WOLF.h - 12) * wf.scale + (WOLF.nose.y - 12) * wf.scale : 0
  const top = wf ? wf.ground - (WOLF.h - 12) * wf.scale : 0
  const m = moon ? sc.moon : null
  // strokes scale down with the wolf: on a small wolf (the phone strip) the rim light would be a hairline and the silhouette would vanish into the sky,
  // so its strokes are thickened until they measure about the same on screen as on the big wolf
  const k = wf ? Math.max(1, 0.5 / wf.scale) : 1
  return (
    <Svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio={`${align} slice`} aria-hidden="true" focusable="false" $still={still} $bleed={bleed} className={className} data-testid="moon-scene">
      <defs>
        <MoonDefs id={id} />
        <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e6f6ff" />
          <stop offset="0.55" stopColor="#7fd4e8" />
          <stop offset="1" stopColor="#8c7cff" />
        </linearGradient>
        <linearGradient id={`${id}-ridge`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b1030" />
          <stop offset="1" stopColor="#03040b" />
        </linearGradient>
        <filter id={`${id}-bloom`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
      </defs>

      {stars.map((s) => (
        <circle key={`${s.x}-${s.y}`} cx={s.x} cy={s.y} r={s.r} fill="#eaf2ff" opacity={s.o} className={s.tw ? `tw tw${s.tw}` : undefined} style={{ ['--o' as string]: s.o }} />
      ))}

      {m && <MoonBody id={id} x={m.x} y={m.y} r={m.r} halo={3.6} />}

      {wf && (
        <>
          {/* the ridge the wolf stands on */}
          <path
            d={ridge(wf.x, wf.ground, wf.scale / 0.6, w, h)}
            fill={`url(#${id}-ridge)`}
          />
          <g transform={`translate(${wf.x - 12 * wf.scale},${top - 12 * wf.scale}) scale(${wf.scale})`} data-testid="wolf">
            <path d={WOLF.rim} stroke="#8fe0ff" strokeWidth={5 * k} strokeLinecap="round" fill="none" opacity={Math.min(0.85, 0.5 + (k - 1) * 0.3)} filter={`url(#${id}-bloom)`} />
            <path d={WOLF.silhouette} fill="#070a1a" />
            {FACET_FILLS.map((f, i) => (
              <path key={f} d={WOLF.facets[i]} fill={f} />
            ))}
            <path d={WOLF.edges} stroke="#7fd4e8" strokeOpacity="0.13" strokeWidth="0.8" fill="none" />
            <path d={WOLF.rim} stroke={`url(#${id}-rim)`} strokeWidth={2.6 * k} strokeLinecap="round" fill="none" />
            <ellipse cx={WOLF.eye.x} cy={WOLF.eye.y} rx="5.5" ry="1.7" transform={`rotate(${WOLF.eye.angle} ${WOLF.eye.x} ${WOLF.eye.y})`} fill="#bfeaff" />
          </g>
          {/* three howl rings leave the muzzle towards the moon */}
          <g transform={`translate(${noseX},${noseY}) rotate(-49) scale(${sc.ring})`} fill="none" stroke="#9fdcff" strokeLinecap="round">
            <path className="ring" d="M46,-30 A56,56 0 0 1 46,30" strokeWidth="2.2" />
            <path className="ring ring2" d="M46,-30 A56,56 0 0 1 46,30" strokeWidth="2" />
            <path className="ring ring3" d="M46,-30 A56,56 0 0 1 46,30" strokeWidth="1.8" />
          </g>
        </>
      )}
    </Svg>
  )
}

/** The sky behind a scene: deep night with a violet haze, lit from the moon's side. Apply to the container that holds <MoonScene />. */
export const nightSky = css`
  background: radial-gradient(90% 120% at 62% 0%, rgba(33, 0, 255, 0.28) 0%, rgba(126, 0, 254, 0.1) 38%, transparent 70%),
    linear-gradient(180deg, #060a1f 0%, #0a1030 58%, #0f1640 100%);
`
